import type { LucideIcon } from "lucide-react";
import {
  CircleDollarSignIcon,
  HeartPulseIcon,
  LayersIcon,
  MegaphoneIcon,
  TrendingUpIcon,
} from "lucide-react";

export const DASHBOARDS_CATALOG_BASE_PATH = "/dashboards" as const;
/** Generated dashboard artifacts live under `/dashboards/custom/<slug>`. */
export const DASHBOARD_ARTIFACTS_SEGMENT = "custom" as const;
export const SALES_ENROLLMENT_DASHBOARD_SLUG = "territory-enrollment-performance" as const;
export const SALES_ENROLLMENT_DASHBOARD_PATH =
  `${DASHBOARDS_CATALOG_BASE_PATH}/${DASHBOARD_ARTIFACTS_SEGMENT}/${SALES_ENROLLMENT_DASHBOARD_SLUG}` as const;

export type DashboardAvailability = "live" | "pending";

export type DashboardCatalogItem = {
  availability: DashboardAvailability;
  color: string;
  description: string;
  face: string;
  label: string;
  /** When false, hide the shared single-page PDF export control. Defaults to true. */
  pdfExport?: boolean;
  /** Path segment under `/dashboards/` (e.g. `eng-velocity`). */
  slug: string;
};

export type DashboardCatalogSection = {
  icon: LucideIcon;
  items: DashboardCatalogItem[];
  title: string;
};

/**
 * Tool-local catalog ported from operators_dashboards `dashboardCatalog.js`.
 * `availability: "live"` means the view is migrated; everything else uses the
 * in-tool placeholder route until its Phase 2/3 ticket lands.
 */
export const DASHBOARD_CATALOG_SECTIONS: DashboardCatalogSection[] = [
  {
    title: "Growth",
    icon: TrendingUpIcon,
    items: [
      {
        slug: "website-traffic",
        label: "Website Traffic",
        description:
          "GA4 sessions, pageviews, active users, bounce rate, top pages, and traffic sources.",
        color: "#00ABE7",
        face: "#E6F8FC",
        availability: "live",
      },
      {
        slug: "sales",
        label: "Sales",
        pdfExport: false,
        description:
          "Account enrollment by sales rep and territory — outreach, intake bookings, enrollment curves, and Excel export.",
        color: "#5C6BC0",
        face: "#E8EBFA",
        availability: "live",
      },
      {
        slug: "account-management",
        label: "Account Management",
        description:
          "Portfolio triage for clinic alignment, data completeness, freshness, attrition, and onboarding health.",
        color: "#8C5E58",
        face: "#F4E8E5",
        availability: "live",
      },
    ],
  },
  {
    title: "Outreach & Enrollment",
    icon: MegaphoneIcon,
    items: [
      {
        slug: "retell-calls",
        label: "Retell Calls",
        description:
          "Daily AI call summary — intake rate, call volume trends, intraday pacing, and outcome breakdown.",
        color: "#0081AF",
        face: "#E9F3F8",
        availability: "live",
      },
      {
        slug: "enrollment",
        label: "Enrollment",
        description: "Patient enrollment metrics, funnel performance, and target progress.",
        color: "#6D6A66",
        face: "#F2F0ED",
        availability: "live",
      },
      {
        slug: "inbound-calls",
        label: "Inbound Calls",
        description:
          "Inbound Dialpad call volume, pickup rates, hourly and weekly trends, top destinations, and answerer performance.",
        color: "#00ABE7",
        face: "#E6F8FC",
        availability: "live",
      },
    ],
  },
  {
    title: "Care Navigation",
    icon: HeartPulseIcon,
    items: [
      {
        slug: "care-navigation",
        label: "Care Navigation",
        description:
          "Care navigator KPIs — encounter completion, needs assessments, care plan updates, attrition, and call minutes.",
        color: "#F7941D",
        face: "#FCE5C5",
        availability: "live",
      },
      {
        slug: "attrition",
        label: "Attrition",
        description:
          "Period-based GUIDE attrition — unaligned and unengaged outcomes as a share of the post-alignment cohort.",
        color: "#C84C35",
        face: "#F8E1DC",
        availability: "live",
      },
      {
        slug: "reengagement-emails",
        label: "Re-engagement Emails",
        description:
          "Monthly campaign report — eligible carespaces, outreach-group coverage, and downstream email, booking, and encounter outcomes.",
        color: "#8E6C8A",
        face: "#F2E8F1",
        availability: "live",
      },
      {
        slug: "respite",
        label: "Respite",
        description:
          "Home safety assessments against the 60-day goal, respite referrals by provider, and open referrals that no longer fit GUIDE respite.",
        color: "#2E8B57",
        face: "#E5F3EA",
        availability: "live",
      },
      {
        slug: "cn-call-review",
        label: "CN Call Review",
        description:
          "Care navigator call reviews — deterministic encounter facts, AI rubric scores, and follow-up flags.",
        color: "#7C3AED",
        face: "#F1EAFE",
        availability: "live",
      },
      {
        slug: "call-audio",
        label: "Call Audio",
        description:
          "Archived Dialpad and Daily call recordings, with on-demand playback for dashboard viewers.",
        color: "#0E7490",
        face: "#E6F6F8",
        availability: "live",
        pdfExport: false,
      },
    ],
  },
  {
    title: "Product",
    icon: LayersIcon,
    items: [
      {
        slug: "product-priorities",
        label: "Product Priorities",
        description:
          "North-star aligned-carespace growth plus the first live care-navigation efficiency metric and explicit instrumentation gaps.",
        color: "#0F766E",
        face: "#E8F6F3",
        availability: "live",
      },
      {
        slug: "eng-velocity",
        label: "Eng Velocity",
        description:
          "Commits, source lines changed, pull requests, and Linear production deploys per engineer across web, marketing-site, operators, operators_dashboards & desktop.",
        color: "#4F5BD5",
        face: "#E8EBFA",
        availability: "live",
      },
      {
        slug: "data-dictionary",
        label: "Data Dictionary",
        description:
          "Interactive production schema explorer with tables, columns, types, and relationships.",
        color: "#2563EB",
        face: "#EAF1FE",
        availability: "live",
        pdfExport: false,
      },
      {
        slug: "cal-killer",
        label: "Cal Killer",
        description:
          "Aggregate Cal.com booking volume, event-type coverage, status mix, and Carespace/encounter linkage health.",
        color: "#0F766E",
        face: "#E8F6F3",
        availability: "live",
      },
      {
        slug: "switchboard",
        label: "Switchboard",
        description:
          "Daily Switchboard time saved, connected and completed care calls, adoption, and reviewed call paths.",
        color: "#0284C7",
        face: "#E9F3F8",
        availability: "live",
        pdfExport: false,
      },
    ],
  },
  {
    title: "Finance",
    icon: CircleDollarSignIcon,
    items: [
      {
        slug: "dcmp-billing",
        label: "DCMP Billing",
        description:
          "Net DCMP billables, patient growth, GUIDE status, eligibility outcomes, and effective clinic payment rates.",
        color: "#004e89",
        face: "#efefd0",
        availability: "live",
      },
      {
        slug: "revenue",
        label: "TIN Revenue",
        description:
          "TIN-scoped revenue dashboard — topline cards, trend, clinic payout view, HCPCS mix, and claim queue trend.",
        color: "#2A9D8F",
        face: "#E6F5F2",
        availability: "live",
      },
    ],
  },
];

const DASHBOARD_ENTRIES = DASHBOARD_CATALOG_SECTIONS.flatMap((section) =>
  section.items.map((item) => ({ ...item, section: section.title })),
);

const DASHBOARD_BY_SLUG = new Map(DASHBOARD_ENTRIES.map((entry) => [entry.slug, entry]));

export function getDashboardPath(slug: string): string {
  return `${DASHBOARDS_CATALOG_BASE_PATH}/${slug}`;
}

/** Dedicated file-route paths for migrated live dashboards (not the `$dashboardId` catch-all). */
export const LIVE_DASHBOARD_ROUTES = {
  "account-management": "/dashboards/account-management",
  attrition: "/dashboards/attrition",
  "care-navigation": "/dashboards/care-navigation",
  "cal-killer": "/dashboards/cal-killer",
  "call-audio": "/dashboards/call-audio",
  switchboard: "/dashboards/switchboard",
  "data-dictionary": "/dashboards/data-dictionary",
  "dcmp-billing": "/dashboards/dcmp-billing",
  enrollment: "/dashboards/enrollment",
  "eng-velocity": "/dashboards/eng-velocity",
  "cn-call-review": "/dashboards/cn-call-review",
  "inbound-calls": "/dashboards/inbound-calls",
  "product-priorities": "/dashboards/product-priorities",
  "reengagement-emails": "/dashboards/reengagement-emails",
  respite: "/dashboards/respite",
  "retell-calls": "/dashboards/retell-calls",
  revenue: "/dashboards/revenue",
  sales: "/dashboards/sales",
  "website-traffic": "/dashboards/website-traffic",
} as const;

export type LiveDashboardSlug = keyof typeof LIVE_DASHBOARD_ROUTES;

export function getLiveDashboardRoute(
  slug: string,
): (typeof LIVE_DASHBOARD_ROUTES)[LiveDashboardSlug] | null {
  if (slug in LIVE_DASHBOARD_ROUTES) {
    return LIVE_DASHBOARD_ROUTES[slug as LiveDashboardSlug];
  }
  return null;
}

export function getDashboardBySlug(slug: string): DashboardCatalogItem | null {
  return DASHBOARD_BY_SLUG.get(slug) ?? null;
}

export function isKnownDashboardSlug(slug: string): boolean {
  return DASHBOARD_BY_SLUG.has(slug);
}

/** Resolve a browser pathname (e.g. `/dashboards/attrition`) to a catalog entry. */
export function getDashboardByPathname(pathname: string): DashboardCatalogItem | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === DASHBOARDS_CATALOG_BASE_PATH) {
    return null;
  }
  const prefix = `${DASHBOARDS_CATALOG_BASE_PATH}/`;
  if (!normalized.startsWith(prefix)) {
    return null;
  }
  const slug = normalized.slice(prefix.length).split("/")[0] ?? "";
  if (!slug) {
    return null;
  }
  return getDashboardBySlug(slug);
}

export function getDashboardPageTitle(pathname: string): string | null {
  const entry = getDashboardByPathname(pathname);
  return entry?.label ?? null;
}
